// @flow
import * as React from 'react';
import classNames from 'classnames/bind';
import memoize from 'memoize-one';
import { VariableSizeList, areEqual } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import AutoSizer from 'react-virtualized-auto-sizer';
import { without, union } from 'ramda';
import ProductRow from './product-row';
import { HeaderRow, HeaderCell } from '../../basic-components/table';
import Loader from '../../basic-components/loader';
import ShowcaseLink from '../stock-table/showcase-link';
import { flex } from '../stock-table/flexconfig';
import AiMassEditRow from './ai-mass-edit-row';
import cs from './styles.pcss';
import t from './locale';

import type {
  Products_products_nodes as ProductT,
  StockData_showcases as ShowcaseT,
  StockData_profiles_nodes as ShippingProfileType,
  UserLimits_viewer_limits as LimitT,
} from '../../typings/product-query.flow';
import type { ProductChange } from '../../typings/product';

const cx = classNames.bind(cs);

// 行高常量
const ROW_DEFAULT_HEIGHT = 51;

type Props = {
  products: Array<ProductT>,
  selectedProductId: string,
  showcases: Array<ShowcaseT>,
  selectedProducts: Set<string>,
  onProductSelect: (
    productId: string,
    checked: boolean,
    preserveSelectAll?: boolean,
  ) => void,
  onToggleAllProducts?: (checked: boolean) => void,
  shippingProfiles?: Array<ShippingProfileType>,
  registrationDate?: string,
  managedPayments: boolean,
  limits: Array<LimitT>,
  checkedVariations?: Set<string>,
  onVariationToggle?: (id: string) => void,
  onLoadMore?: () => Promise<void>,
  hasNextPage?: boolean,
  loading?: boolean,
  changes?: Array<ProductChange>,
};

// ItemRenderer - 只渲染产品行和 loader
/* eslint-disable react/prop-types */
const ItemRenderer = React.memo(({ data, index, style }): React.Node => {
  const {
    items,
    showcases,
    checkedVariations,
    openedVariations,
    onToggle,
    onVariationToggle,
    onVariationsOpenToggle,
    checkedProducts,
  } = data;
  const item = items[index];

  // 处理特殊行类型
  if (typeof item === 'string') {
    // Header and mass edit rows are rendered in innerElementType
    if (item === 'headerRow' || item === 'massEditRow') {
      return null;
    }

    // Loader row
    if (item === 'loaderRow') {
      return (
        <div style={style} className={cx('loader-row')}>
          <Loader size={24} />
        </div>
      );
    }

    // No more data row
    if (item === 'noMoreRow') {
      return (
        <div style={style} className={cx('no-more-data')}>
          {t('noMoreData')}
        </div>
      );
    }

    return null;
  }

  // Product row
  return (
    <div style={style}>
      <ProductRow
        key={item.id}
        item={item}
        showcases={showcases}
        checkedVariations={checkedVariations}
        openedVariations={openedVariations}
        onToggle={onToggle}
        onVariationToggle={onVariationToggle}
        onVariationsOpenToggle={onVariationsOpenToggle}
        onClick={() => {}}
        checkedProducts={checkedProducts}
      />
    </div>
  );
}, areEqual);
/* eslint-enable react/prop-types */

// 使用 memoize-one 创建 itemData
const createItemData = memoize((props) => props);

// Constants
const HEADER_HEIGHT = 51;
const MASS_EDIT_ROW_HEIGHT = 51;

// Context for passing header and mass edit row to innerElementType
const TableContext = React.createContext<{
  headerContent?: React.Node,
  massEditContent?: React.Node,
}>({});

// Custom inner element that includes fixed header and mass edit row
const InnerElementType = React.forwardRef(({ children, ...rest }: any, ref) => {
  const { headerContent, massEditContent } = React.useContext(TableContext);

  return (
    <div ref={ref} {...rest}>
      {headerContent}
      {massEditContent}
      {children}
    </div>
  );
});

export default function ProductsTableView({
  products,
  showcases,
  selectedProducts,
  onProductSelect,
  onToggleAllProducts,
  registrationDate,
  managedPayments,
  limits,
  checkedVariations = new Set(),
  onVariationToggle,
  onLoadMore,
  hasNextPage = false,
  loading = false,
  changes = [],
}: Props): React.Node {
  const listRef = React.useRef(null);
  const [openedVariations, setOpenedVariations] = React.useState<string[]>([]);

  // 构建 items 数组 - 包含占位符行、产品数据、loader 或 noMoreRow
  const items = React.useMemo(() => {
    const result = ['headerRow', 'massEditRow', ...products];
    if (hasNextPage) {
      result.push('loaderRow');
    } else if (products.length > 0) {
      result.push('noMoreRow');
    }
    return result;
  }, [products, hasNextPage]);

  const allSelected =
    products.length > 0 && selectedProducts.size === products.length;

  // Toggle All - 批量选择/取消所有
  const handleToggleAll = () => {
    const shouldCheck = !allSelected;
    onToggleAllProducts?.(shouldCheck);
    products.forEach((product) => {
      onProductSelect(product.id, shouldCheck, true);
    });
  };

  // 单个产品选择处理
  const handleToggle = (id: string) => {
    const isSelected = selectedProducts.has(id);
    onProductSelect(id, !isSelected);
  };

  // Variations 展开/折叠处理
  const handleVariationsOpenToggle = (id: string) => {
    setOpenedVariations((prevOpenedVariations) => {
      const index = prevOpenedVariations.indexOf(id);
      if (index > -1) {
        return without([id], prevOpenedVariations);
      }
      return union([id], prevOpenedVariations);
    });

    // 重置行高缓存 (需要考虑前面有2个占位符行)
    if (listRef && listRef.current) {
      const itemIndex = items.findIndex(
        (item) => typeof item !== 'string' && item.id === id,
      );
      if (itemIndex > -1) {
        (listRef.current: any).resetAfterIndex(itemIndex, false);
      }
    }
  };

  // 计算每个项的高度
  const getItemSize = (index: number): number => {
    const item = items[index];

    // Header row
    if (item === 'headerRow') return HEADER_HEIGHT;

    // Mass edit row
    if (item === 'massEditRow') return MASS_EDIT_ROW_HEIGHT;

    // Loader row
    if (item === 'loaderRow') return ROW_DEFAULT_HEIGHT;

    // No more data row
    if (item === 'noMoreRow') return ROW_DEFAULT_HEIGHT;

    // Product row
    const product = item;
    const isOpened = openedVariations.includes(product.id);
    const hasVariations = product.variations && product.variations.length > 1;

    let height = ROW_DEFAULT_HEIGHT;
    if (isOpened && hasVariations) {
      height += product.variations.length * ROW_DEFAULT_HEIGHT;
    }

    return height;
  };

  // itemCount
  const itemCount = items.length;

  // 判断项是否已加载
  const isItemLoaded = (index: number) => {
    const item = items[index];
    if (typeof item === 'string') return item !== 'loaderRow';
    return true;
  };

  // 加载更多
  const loadMoreItems = loading || !onLoadMore ? () => {} : onLoadMore;

  // itemKey - 类似 stock-table
  const itemKey = React.useCallback((index: number, data: any) => {
    const item = data.items[index];
    if (typeof item === 'string') return item;
    return item.id;
  }, []);

  // 转换 Set 为数组
  const checkedVariationsArray = React.useMemo(
    () => Array.from(checkedVariations),
    [checkedVariations],
  );

  const checkedProductsArray = React.useMemo(
    () => Array.from(selectedProducts),
    [selectedProducts],
  );

  const selectedCount = React.useMemo(() => {
    const selectedIds = new Set(checkedProductsArray);

    products.forEach((product) => {
      if (selectedIds.has(product.id)) return;

      const variations = product.variations || [];
      const hasVariations = variations.length > 1;
      if (!hasVariations) return;

      variations.forEach((variation) => {
        if (checkedVariations.has(variation.id)) {
          selectedIds.add(variation.id);
        }
      });
    });

    return selectedIds.size;
  }, [checkedProductsArray, checkedVariations, products]);

  // 使用 createItemData 创建 itemData
  const itemData = createItemData({
    items,
    showcases,
    checkedVariations: checkedVariationsArray,
    openedVariations,
    onToggle: handleToggle,
    onVariationToggle,
    onVariationsOpenToggle: handleVariationsOpenToggle,
    checkedProducts: checkedProductsArray,
  });

  // Convert checkedVariations array back to Set for MassEditRow
  const checkedVariationsSet = new Set(checkedVariations);

  // Check if a showcase has changes
  const hasShowcaseChanges = (showcaseId: string) =>
    changes.some(
      (change) => change.showcaseId === showcaseId && !change.ignore,
    );

  // Prepare header content
  const headerContent = (
    <div className={cx('fixed-header')}>
      <HeaderRow largePadding className={cx('header-row')}>
        <HeaderCell {...flex('title')}>
          <span>{t('title')}</span>
        </HeaderCell>
        <HeaderCell {...flex('sku')}>{t('sku')}</HeaderCell>
        <HeaderCell {...flex('amount')}>{t('quantity')}</HeaderCell>
        {showcases.map(
          ({
            site,
            id,
            active,
            name,
            anyImported,
            anySelectable,
            anySelected,
          }) => {
            const hasChanges = hasShowcaseChanges(id);
            const hasListed = anySelected;

            // changed 状态优先级高于 listed
            let className = '';
            if (hasChanges) {
              className = cx('changed');
            } else if (hasListed) {
              className = cx('listed');
            }

            return (
              <HeaderCell key={id} {...flex('listing')} className={className}>
                <ShowcaseLink
                  site={site}
                  active={active}
                  id={id}
                  name={name}
                  anyImported={anyImported}
                  anySelectable={anySelectable}
                  anySelected={anySelected}
                  registrationDate={registrationDate}
                  managedPayments={managedPayments}
                  limits={limits}
                />
              </HeaderCell>
            );
          },
        )}
      </HeaderRow>
    </div>
  );

  // Prepare mass edit content
  const massEditContent = (
    <div className={cx('fixed-mass-edit')}>
      <AiMassEditRow
        showcases={showcases}
        selectedCount={selectedCount}
        checkedProducts={checkedProductsArray}
        checkedVariations={checkedVariationsSet}
        onToggleAll={handleToggleAll}
        checkedAll={allSelected}
      />
    </div>
  );

  // Context value for TableContext
  const contextValue = React.useMemo(
    () => ({ headerContent, massEditContent }),
    [headerContent, massEditContent],
  );

  return (
    <div className={cx('table-container')}>
      {/* Scrollable Products Table */}
      <TableContext.Provider value={contextValue}>
        <div className={cx('products-table')}>
          <AutoSizer>
            {({ height, width }) =>
              onLoadMore ? (
                <InfiniteLoader
                  isItemLoaded={isItemLoaded}
                  itemCount={itemCount}
                  loadMoreItems={loadMoreItems}
                  threshold={5}
                >
                  {({ onItemsRendered, ref }) => (
                    <VariableSizeList
                      height={height}
                      width={width}
                      itemCount={itemCount}
                      itemSize={getItemSize}
                      itemKey={itemKey}
                      onItemsRendered={onItemsRendered}
                      ref={(el) => {
                        ref(el);
                        listRef.current = el;
                      }}
                      itemData={itemData}
                      overscanCount={5}
                      innerElementType={InnerElementType}
                    >
                      {ItemRenderer}
                    </VariableSizeList>
                  )}
                </InfiniteLoader>
              ) : (
                <VariableSizeList
                  height={height}
                  width={width}
                  itemCount={itemCount}
                  itemSize={getItemSize}
                  itemKey={itemKey}
                  ref={listRef}
                  itemData={itemData}
                  overscanCount={5}
                  innerElementType={InnerElementType}
                >
                  {ItemRenderer}
                </VariableSizeList>
              )
            }
          </AutoSizer>
        </div>
      </TableContext.Provider>
    </div>
  );
}
